import { useState } from "react";
import "./EditPortfolioModal.styles.scss";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { EditPortfolioModalProps } from "./EditPortfolioModal.types";
import { useUpdatePortfolioMutation } from "../../services/entityManagementApi";
import { useAlerts } from "@/hooks/useAlerts";
import { useAppContext } from "@/context/context";

/**
 * EditPortfolioModal component
 * Modal for editing portfolio names
 * Uses RTK Query mutation with automatic cache invalidation
 */
const EditPortfolioModal = ({
  open,
  portfolioId,
  portfolioName,
  closeModal,
}: EditPortfolioModalProps) => {
  const { showError, showSuccess } = useAlerts();
  const { context_state } = useAppContext();
  const orgId = context_state?.org_metadata?.org_id;

  const [updatePortfolio, { isLoading }] = useUpdatePortfolioMutation();
  const [newName, setNewName] = useState(portfolioName);

  const handleSave = async () => {
    try {
      if (!orgId) {
        showError("Organization ID not found");
        return;
      }
      await updatePortfolio({
        orgId,
        portfolioId,
        data: { display_name: newName.trim() },
      }).unwrap();
      showSuccess("Portfolio updated successfully");
      closeModal();
    } catch (error: any) {
      showError(
        error?.data?.message || error?.message || "Failed to update portfolio",
      );
    }
  };

  const handleClose = () => {
    setNewName(portfolioName);
    closeModal();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="edit-portfolio-modal-dialog-title"
      aria-describedby="edit-portfolio-modal-dialog-description"
      className="edit-portfolio-modal"
      fullWidth
    >
      <DialogTitle id="edit-portfolio-modal-dialog-title">
        Edit Portfolio
      </DialogTitle>
      <DialogContent>
        <p className="pulse-typography-subtitle-2 pulse-color--background-100 pulse-mt-2 pulse-mb-1 pulse-pl-1">
          Portfolio Name
        </p>
        <input
          name="portfolio-name"
          className="pulse-ml-1"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="portfolio..."
        />
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          className="edit-portfolio-modal-cancel-button"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          className="edit-portfolio-modal-save-button"
          disabled={isLoading || newName.trim().length === 0}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditPortfolioModal;
